/**
 * Audit Readiness Trend card: line chart of auditReadinessScore over time with
 * 30/90 day view toggle and a plain-English caption describing the trend.
 */
import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardBody } from '../ui';
import { SimpleLineChart } from './SimpleLineChart';

function getTrendCaption(values, days) {
  if (!values.length) return `No snapshot data for the last ${days} days yet. Run your first nightly snapshot or add more history.`;
  const first = values[0];
  const last = values[values.length - 1];
  const delta = last - first;
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  if (delta > 5) return `Audit readiness is trending up over the last ${days} days. You're in better shape for an audit.`;
  if (delta < -5) return `Audit readiness has dipped. Focus on renewing expiring items or filling gaps to get back on track.`;
  return `Audit readiness has held steady (around ${Math.round(avg)}%). You're maintaining a solid baseline.`;
}

export default function AuditReadinessTrendCard({ snapshots, loading }) {
  const [range, setRange] = useState(30); // 30 or 90
  const values = useMemo(() => {
    if (!snapshots?.length) return [];
    const slice = snapshots.slice(0, range === 30 ? 30 : 90);
    return slice
      .map((s) => (s.auditReadinessScore != null ? Number(s.auditReadinessScore) : null))
      .filter((v) => v != null)
      .reverse();
  }, [snapshots, range]);
  const caption = useMemo(() => getTrendCaption(values, range), [values, range]);

  if (loading) {
    return (
      <Card className="analytics-card audit-readiness-trend-card">
        <CardBody>
          <div className="analytics-card-loading">
            <div className="loading-spinner" />
            <span>Loading trend...</span>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="analytics-card audit-readiness-trend-card">
      <CardHeader
        title="Audit readiness trend"
        subtitle={`Score over time`}
        action={
          <div className="analytics-range-tabs">
            <button
              type="button"
              className={range === 30 ? 'active' : ''}
              onClick={() => setRange(30)}
              aria-pressed={range === 30}
            >
              30 days
            </button>
            <button
              type="button"
              className={range === 90 ? 'active' : ''}
              onClick={() => setRange(90)}
              aria-pressed={range === 90}
            >
              90 days
            </button>
          </div>
        }
      />
      <CardBody>
        <SimpleLineChart values={values} strokeColor="var(--amber)" caption={caption} />
      </CardBody>
    </Card>
  );
}
